import { useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  FlaskConical,
  Paperclip,
  Pencil,
  Plus,
  TestTube,
  Trash2,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getExamesControllerListarQueryKey,
  useExamesControllerExcluir,
  useExamesControllerListar,
} from '@/api/generated/exames/exames';
import { ApiError } from '@/api/http-client';
import type { ExameResponse, ExameResponseOrigem } from '@/api/generated/model';
import { ExameFormDialogo } from '@/features/exames/components/ExameFormDialogo';
import { ResultadosDialogo } from '@/features/exames/components/ResultadosDialogo';
import { formatarDataHora, ORIGENS_FILTRAVEIS, rotuloDeOrigem } from '@/features/exames/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDoExame } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';

const TODOS = 'todos';

/**
 * Exames (F3-4).
 *
 * ⚠️ Sem filtro de ambiente: o escopo sai da sessão, no servidor. Vale para o
 * super_admin também.
 */
export function ExamesPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [origem, setOrigem] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<ExameResponse | undefined>();
  const [resultadosDe, setResultadosDe] = useState<ExameResponse | null>(null);
  const [aExcluir, setAExcluir] = useState<ExameResponse | null>(null);

  const { data, isPending, isError, refetch } = useExamesControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'criadoEm',
    // Explícito: o default do schema de paginação é `asc`, e uma lista de
    // exames que abre no mais antigo é o avesso do que se procura.
    ordem: 'desc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(origem !== TODOS ? { origem: origem as ExameResponseOrigem } : {}),
  });

  const excluir = useExamesControllerExcluir();

  const podeCriar = pode('exames.criar');
  const podeEditar = pode('exames.editar');
  const podeExcluir = pode('exames.excluir');
  const podeLancar = pode('resultados.editar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function confirmarExclusao(exame: ExameResponse) {
    try {
      await excluir.mutateAsync({ id: exame.id });
      await queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() });
      toast.success('Exame excluído.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir o exame.');
    } finally {
      setAExcluir(null);
    }
  }

  return (
    <TooltipProvider>
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por OS, paciente ou exame..."
            className="sm:max-w-xs"
          />

          <Select value={origem} onValueChange={(v) => aoFiltrar(() => setOrigem(v))}>
            <SelectTrigger className="sm:w-48" aria-label="Filtrar por origem">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todas as origens</SelectItem>
              {ORIGENS_FILTRAVEIS.map((o) => (
                <SelectItem key={o} value={o}>
                  {rotuloDeOrigem(o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Novo exame
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Exames</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={FlaskConical}
            titulo="Nenhum exame encontrado"
            descricao={
              busca || origem !== TODOS
                ? 'Tente outro termo ou limpe os filtros.'
                : 'Cadastre o primeiro exame para começar.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Exame</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead className="text-right">Resultados</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((exame) => (
                    <TableRow key={exame.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{exame.pacienteNome}</span>
                        {exame.os && (
                          <p className="text-xs text-muted-foreground mt-0.5">OS {exame.os}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-foreground">{exame.exameNome}</span>
                        {exame.urgente && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-destructive border-destructive/40"
                          >
                            <Zap className="w-3 h-3 mr-1" />
                            Urgente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {rotuloDeOrigem(exame.origem)}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Exame externo não tem analito: o resultado dele é o
                            documento anexado, e "0/0" seria uma contagem sem
                            sentido. Mesma leitura da aba de exames do paciente. */}
                        {exame.totalAnalitos === 0 ? (
                          exame.anexos.length > 0 ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
                              {exame.anexos.length}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : exame.origem === 'EXTERNO' ? (
                          // No externo não há item de catálogo a completar:
                          // "3/3" leria como "nada falta", e o que existe é
                          // "o laudo trouxe 3 valores".
                          <span className="text-muted-foreground">
                            {exame.resultados.length}{' '}
                            {exame.resultados.length === 1 ? 'valor' : 'valores'}
                          </span>
                        ) : (
                          <span
                            className={
                              exame.resultados.length >= exame.totalAnalitos
                                ? 'font-medium text-emerald-600'
                                : 'text-muted-foreground'
                            }
                          >
                            {exame.resultados.length}/{exame.totalAnalitos}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {/* Primeiro botão, como na V1: lá "Laudo" era a
                              única ação da linha. Leva ao laudo completo. */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" asChild>
                                <Link
                                  to={rotaDoExame(exame.id)}
                                  aria-label={`Ver detalhes de ${exame.exameNome} de ${exame.pacienteNome}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Ver detalhes</TooltipContent>
                          </Tooltip>

                          {/* Sempre presente: quem não pode editar ainda
                              precisa LER o resultado. O dialog decide. */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Resultados de ${exame.exameNome} de ${exame.pacienteNome}`}
                                onClick={() => setResultadosDe(exame)}
                              >
                                <TestTube className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Resultados</TooltipContent>
                          </Tooltip>

                          {podeEditar && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Editar ${exame.exameNome} de ${exame.pacienteNome}`}
                                  onClick={() => {
                                    setEmEdicao(exame);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar</TooltipContent>
                            </Tooltip>
                          )}

                          {podeExcluir && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Excluir ${exame.exameNome} de ${exame.pacienteNome}`}
                                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setAExcluir(exame)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Excluir</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      {/* O corte do sync não é detalhe de implementação: sem este aviso, o
          veterinário procura um exame antigo do laboratório, não acha, e
          conclui que o sistema perdeu o dado. */}
      {data?.examesDesde && (
        <p className="text-xs text-muted-foreground">
          {`Exames do laboratório anteriores a ${formatarDataHora(data.examesDesde)} não são importados. Lançamentos manuais não têm esse corte.`}
        </p>
      )}

      <ExameFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { exame: emEdicao } : {})}
      />

      {resultadosDe && (
        <ResultadosDialogo
          aberto
          aoFechar={() => setResultadosDe(null)}
          exame={resultadosDe}
          podeEditar={podeLancar}
        />
      )}

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este exame?</AlertDialogTitle>
            <AlertDialogDescription>
              {aExcluir && aExcluir.resultados.length > 0
                ? `${aExcluir.resultados.length} resultado(s) já lançado(s) vão junto, e a interpretação calculada se perde. Não há como desfazer.`
                : 'O exame sai do histórico do paciente. Não há como desfazer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aExcluir && void confirmarExclusao(aExcluir)}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}
